import { useCallback, useEffect, useMemo, useState } from 'react';
import { InsightNotificationsApiClient } from '../api/notificationsClient';
import type { InsightNotification } from '../notificationTypes';

interface UseInsightNotificationsInput {
  projectId: string;
  enabled: boolean;
  /** Poll interval; the center is near-real-time without websockets. */
  pollIntervalMs?: number;
}

interface UseInsightNotificationsResult {
  notifications: InsightNotification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  markRead: (notificationId: string) => Promise<void>;
  markAllRead: () => Promise<void>;
}

const DEFAULT_POLL_INTERVAL_MS = 60_000;

export function useInsightNotifications(input: UseInsightNotificationsInput): UseInsightNotificationsResult {
  const [notifications, setNotifications] = useState<InsightNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const client = useMemo(() => new InsightNotificationsApiClient(), []);

  useEffect(() => {
    if (!input.enabled || !input.projectId) return;

    const controller = new AbortController();
    let cancelled = false;

    const load = () => {
      setLoading(true);
      client.list(input.projectId, { limit: 50 }, controller.signal)
        .then((items) => {
          if (!cancelled) {
            setNotifications(items);
            setError(null);
          }
        })
        .catch((err) => {
          if (controller.signal.aborted || cancelled) return;
          setError(err instanceof Error ? err.message : 'Failed to load notifications');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    };

    load();
    const interval = setInterval(load, input.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      controller.abort();
      clearInterval(interval);
    };
  }, [client, input.enabled, input.projectId, input.pollIntervalMs, refreshKey]);

  const refresh = useCallback(() => setRefreshKey(key => key + 1), []);

  const markRead = useCallback(async (notificationId: string) => {
    await client.markRead(input.projectId, notificationId);
    setNotifications(current => current.map(notification =>
      notification.id === notificationId ? { ...notification, status: 'read' as const } : notification,
    ));
  }, [client, input.projectId]);

  const markAllRead = useCallback(async () => {
    await client.markAllRead(input.projectId);
    setNotifications(current => current.map(notification => ({ ...notification, status: 'read' as const })));
  }, [client, input.projectId]);

  const unreadCount = notifications.filter(notification => notification.status === 'unread').length;

  return { notifications, unreadCount, loading, error, refresh, markRead, markAllRead };
}
